import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { BadgePercent, Check, CheckCheck, Crown, PackageOpen, Search } from 'lucide-react';
import styles from './LiveBill.module.css';
import logo from '../../assets/hirsta-logo-white.png';

// One bill being made, step by step: client -> service -> membership discount -> package service -> sent on WhatsApp
const PHONE = '98765 43210';
const STEP_MS = [1500, 1100, 1100, 1200, 1200, 1100, 2800]; // how long each step stays
const LAST = STEP_MS.length - 1;

const lineAnim = {
    initial: { opacity: 0, y: 10, filter: 'blur(4px)' },
    animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
    exit: { opacity: 0, transition: { duration: 0.15 } },
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
};

export default function LiveBill() {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const inView = useInView(ref, { amount: 0.4 });
    const [step, setStep] = useState(reduce ? LAST : 0);
    const [typed, setTyped] = useState(reduce ? PHONE.length : 0);

    // typing the phone number (step 0)
    useEffect(() => {
        if (reduce || step !== 0 || !inView) return undefined;
        setTyped(0);
        const id = setInterval(() => setTyped((t) => (t >= PHONE.length ? t : t + 1)), 85);
        return () => clearInterval(id);
    }, [step, inView, reduce]);

    // move to the next step; loop at the end
    useEffect(() => {
        if (reduce || !inView) return undefined;
        const t = setTimeout(() => setStep((s) => (s >= LAST ? 0 : s + 1)), STEP_MS[step]);
        return () => clearTimeout(t);
    }, [step, inView, reduce]);

    const total = step >= 3 ? 1080 : step >= 2 ? 1200 : 0;

    return (
        <div className={styles.stage} ref={ref} aria-label="Example: a bill being created in Hirsta">
            <div className={styles.card}>
                <div className={styles.head}>
                    <div>
                        <span className={styles.headLabel}>New bill</span>
                        <strong className={styles.invoiceNo}>#2610071042</strong>
                    </div>
                    <span className={`${styles.status} ${step >= 5 ? styles.statusDone : ''}`}>
                        {step >= 5 ? <><Check size={11} strokeWidth={3} /> Completed</> : 'Draft'}
                    </span>
                </div>

                {/* client */}
                <div className={styles.search}>
                    <Search size={13} />
                    <span>{PHONE.slice(0, typed) || <em>Client mobile number</em>}</span>
                    {step === 0 && !reduce && <i className={styles.caret} />}
                </div>
                <AnimatePresence>
                    {step >= 1 && (
                        <motion.div className={styles.client} {...lineAnim}>
                            <span className={styles.avatar}>PS</span>
                            <span className={styles.clientMain}>
                                <strong>Priya Sharma</strong>
                                <small>14 visits, last 12 days ago</small>
                            </span>
                            <span className={styles.tagGold}><Crown size={10} /> Gold</span>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* lines */}
                <div className={styles.lines}>
                    <AnimatePresence>
                        {step >= 2 && (
                            <motion.div className={styles.line} {...lineAnim} key="spa">
                                <span><strong>Hair Spa</strong><small>Served by Riya</small></span>
                                <b>₹1,200</b>
                            </motion.div>
                        )}
                        {step >= 3 && (
                            <motion.div className={`${styles.line} ${styles.lineGood}`} {...lineAnim} key="disc">
                                <span><strong>Gold membership, 10% off</strong><small>Applied automatically</small></span>
                                <b>−₹120</b>
                            </motion.div>
                        )}
                        {step >= 4 && (
                            <motion.div className={styles.line} {...lineAnim} key="pkg">
                                <span><strong>O3+ Facial</strong><small className={styles.pkgNote}><PackageOpen size={10} /> From Bridal Glow, 3 left</small></span>
                                <b className={styles.free}>₹0</b>
                            </motion.div>
                        )}
                    </AnimatePresence>
                    {step < 2 && <div className={styles.placeholder}>Services, products and packages appear here</div>}
                </div>

                <div className={styles.foot}>
                    <span>Net payable</span>
                    <motion.strong key={total} initial={{ opacity: 0.3, y: 4 }} animate={{ opacity: 1, y: 0 }}>₹{total.toLocaleString('en-IN')}</motion.strong>
                </div>
                <motion.div
                    className={styles.cta}
                    animate={step === 5 ? { scale: [1, 0.96, 1] } : { scale: 1 }}
                    transition={{ duration: 0.35 }}
                >
                    {step >= 5 ? <><CheckCheck size={14} /> Bill completed</> : 'Complete bill'}
                </motion.div>
            </div>

            {/* WhatsApp delivery */}
            <AnimatePresence>
                {step >= 6 && (
                    <motion.div
                        className={styles.toast}
                        initial={{ opacity: 0, x: 24, y: 10, scale: 0.94 }}
                        animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, transition: { duration: 0.2 } }}
                        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                    >
                        <div className={styles.preview}>
                            <span className={styles.previewLogo}><img src={logo} alt="" /></span>
                            <span>
                                <strong>Invoice, ₹1,080</strong>
                                <small>Your salon, paid</small>
                            </span>
                        </div>
                        <p>Hi Priya, thank you for visiting! View and download your bill.</p>
                        <span className={styles.ticks}>Sent on WhatsApp <CheckCheck size={12} /></span>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {step >= 3 && step < 6 && (
                    <motion.div
                        className={styles.hint}
                        initial={{ opacity: 0, y: 8, x: '-50%' }}
                        animate={{ opacity: 1, y: 0, x: '-50%' }}
                        exit={{ opacity: 0, transition: { duration: 0.2 } }}
                    >
                        {step >= 4 ? <PackageOpen size={13} /> : <BadgePercent size={13} />} {step >= 4 ? 'Package service added at ₹0' : 'Member discount applied'}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
